import { logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { analytics } from "@/firebase";

/**
 * Set user properties for GA4 tracking
 * Call this after user authentication and data loading
 */
export const setAnalyticsUser = (userData) => {
    if (!analytics || !userData) return;

    // Set the user ID for cross-device tracking
    setUserId(analytics, userData.uid);

    // Set custom user properties for filtering/segmentation
    setUserProperties(analytics, {
        user_name: `${userData.firstName} ${userData.lastName}`.trim(),
        agency_name: userData.agencyName || '',
        agency_id: userData.agencyId || '',
        referral_source_id: userData.referralSourceId || '',
        referral_source_name: userData.referralSourceName || '',
        user_role: userData.isAdmin ? 'admin' : 'standard'
    });
};

/**
 * Clear user properties on logout
 */
export const clearAnalyticsUser = () => {
    if (!analytics) return;

    setUserId(analytics, null);
    setUserProperties(analytics, {
        user_name: null,
        agency_name: null,
        agency_id: null,
        referral_source_id: null,
        referral_source_name: null,
        user_role: null
    });
};

// ============================================
// Authentication Events
// ============================================

export const trackLogin = (method = 'email') => {
    logEvent(analytics, 'login', { method });
};

export const trackLogout = () => {
    logEvent(analytics, 'logout');
};

export const trackPasswordChanged = (success = true) => {
    logEvent(analytics, 'password_changed', { success });
};

// ============================================
// Quote Lifecycle Events
// ============================================

export const trackQuoteStarted = (policyType) => {
    logEvent(analytics, 'quote_started', {
        policy_type: policyType
    });
};

export const trackQuoteStepCompleted = (stepNumber, policyType) => {
    logEvent(analytics, 'quote_step_completed', {
        step_number: stepNumber,
        policy_type: policyType
    });
};

export const trackQuoteSubmitted = (params) => {
    const eventParams = {
        policy_type: params.policyType,
        carrier: params.carrier,
        state: params.state
    };
    if (params.premium) {
        eventParams.premium = params.premium;
    }
    logEvent(analytics, 'quote_submitted', eventParams);
};

export const trackQuoteSubmissionError = (policyType, errorType) => {
    logEvent(analytics, 'quote_submission_error', {
        policy_type: policyType,
        error_type: errorType
    });
};

export const trackQuoteViewed = (quoteId, policyType, status) => {
    logEvent(analytics, 'quote_viewed', {
        quote_id: quoteId,
        policy_type: policyType,
        status: status
    });
};

// ============================================
// Policy Lifecycle Events
// ============================================

export const trackPolicyBindStarted = (quoteId, policyType) => {
    logEvent(analytics, 'policy_bind_started', {
        quote_id: quoteId,
        policy_type: policyType
    });
};

export const trackPolicyBound = (params) => {
    logEvent(analytics, 'policy_bound', {
        policy_id: params.policyId,
        quote_id: params.quoteId,
        premium: params.premium,
        payment_plan: params.paymentPlan,
        carrier: params.carrier,
        policy_type: params.policyType
    });
};

export const trackPolicyViewed = (policyId, policyType) => {
    logEvent(analytics, 'policy_viewed', {
        policy_id: policyId,
        policy_type: policyType
    });
};

// ============================================
// Dashboard Events
// ============================================

export const trackQuotesListViewed = (count) => {
    logEvent(analytics, 'quotes_list_viewed', {
        count: count
    });
};

export const trackPoliciesListViewed = (count) => {
    logEvent(analytics, 'policies_list_viewed', {
        count: count
    });
};

export const trackClaimsPortalClicked = (carrier) => {
    logEvent(analytics, 'claims_portal_clicked', {
        carrier: carrier
    });
};

// ============================================
// Admin Events
// ============================================

export const trackUserCreated = (agencyId) => {
    logEvent(analytics, 'user_created', {
        agency_id: agencyId
    });
};

export const trackAgencyCreated = (agencyName) => {
    logEvent(analytics, 'agency_created', {
        agency_name: agencyName
    });
};

export const trackUsersListViewed = (count) => {
    logEvent(analytics, 'users_list_viewed', {
        count: count
    });
};

export const trackAgenciesListViewed = (count) => {
    logEvent(analytics, 'agencies_list_viewed', {
        count: count
    });
};

// ============================================
// Public Site Events
// ============================================

export const trackContactFormSubmitted = (hasAttachments = false) => {
    logEvent(analytics, 'contact_form_submitted', {
        has_attachments: hasAttachments
    });
};

export const trackVideoPlayed = (videoName) => {
    logEvent(analytics, 'video_played', {
        video_name: videoName
    });
};

export const trackCtaClicked = (ctaType) => {
    logEvent(analytics, 'cta_clicked', {
        cta_type: ctaType
    });
};

// ============================================
// Page View Tracking
// ============================================

export const trackPageView = (pageTitle, pagePath) => {
    if (!analytics) return;

    logEvent(analytics, 'page_view', {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: pagePath
    });
};
